import { isEqual } from "lodash-es";
import { useEffect } from "react";

import usePrevious from "hooks/usePrevious";
import { SearchQueryRequestedPage } from "types/generated";

type UsePaginationCursorResetProps = {
  resetKey: unknown;
  cursor: string | null;
  requestedPage: SearchQueryRequestedPage;
  resetPagination: () => void;
};

/**
 * Keeps a module-scoped pagination store in sync with the query it paginates.
 *
 * The returned cursor already accounts for a `resetKey` that changed in this very render, so the
 * query never goes out with the new filters and the previous page's cursor. The store itself is
 * cleared in an effect, and again on unmount — it outlives the component, so otherwise a remount
 * would query the cursor from the previous visit while the table starts back on page 1.
 */
const usePaginationCursorReset = ({
  resetKey,
  cursor,
  requestedPage,
  resetPagination,
}: UsePaginationCursorResetProps) => {
  const previousResetKey = usePrevious(resetKey);
  const isResetPending = !isEqual(previousResetKey, resetKey);

  useEffect(() => {
    if (isResetPending) {
      resetPagination();
    }
  }, [isResetPending, resetPagination]);

  useEffect(() => resetPagination, [resetPagination]);

  return {
    cursor: isResetPending ? null : cursor,
    requestedPage: isResetPending ? SearchQueryRequestedPage.First : requestedPage,
  };
};

export default usePaginationCursorReset;
